import {
  ActivityType,
  Case,
  CreatePreparedFormMutation,
  CustomUpdatePreparedFormMutation,
  GetFirmQuery,
  PreparedForm,
  PreparedFormState,
  PreparedFormTemplate,
  PreparedFormTemplateInput,
  UpdatePreparedFormInput,
} from 'API';
import { listPreparedFormTemplates } from 'graphql/customQueries';
import { listFormComments, updatePreparedForm } from 'graphql/preparedFormQueries';
import ActivityService from 'services/activity';
import CaseService from 'services/case';
import CommentService from 'services/comment';
import PersonService from 'services/person';
import { getCacheWithExpiration, shortOrLongDate } from 'shared/utilities';
import { createPreparedForm, updateFirm } from 'graphql/mutations';
import { resolvers } from 'shared/enums';
import { FIRM_COMPANY } from './firm';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';

const stateResolver = resolvers.PreparedFormState;
const createComment = CommentService.create;
const { fullname } = PersonService;

class PreparedFormService {
  addFormToCase = (thisCase: Case, template: PreparedFormTemplate) => client.graphql({
    query: createPreparedForm,
    variables: {
      input: {
        caseID: thisCase.id,
        companyID: thisCase.companyID || null,
        foreignNationalID: thisCase.foreignNationalID,
        relativeAdmins: thisCase.relativeAdmins,
        state: PreparedFormState.TODO,
        templateID: template.id,
        requiresAttorneyApproval: template.requiresAttorneyApproval,
        requiresSponsorApproval: template.requiresSponsorApproval,
        requiresNationalApproval: template.requiresNationalApproval,
        employersCanView: template.employersCanView,
        nationalsCanView: template.nationalsCanView,
      },
    },
  }) as unknown as GraphQLResult<CreatePreparedFormMutation>;

  approve = async (
    formID: string,
    approverType: PreparedFormApproverType,
    thisCase: Case,
    formName: string,
    comment?: string,
  ) => {
    const personID = PersonService.getPersonID();
    const personType = PersonService.getPersonType();
    const now = new Date().toISOString();
    const input: UpdatePreparedFormInput = {
      id: formID,
      state: PreparedFormState.PREPARED,
      assignedToID: null,
      assignedToEmployerID: null,
      assignedToNationalID: null,
      assignedToTeamMemberID: null,
      updatedAt: now,
    };

    switch (approverType) {
      case 'sponsor':
        input.sponsorApproverID = personID;
        input.sponsorApproverNationalID = personID;
        input.sponsorApproverEmployerID = personID;
        input.sponsorApprovalDate = now;
        break;

      case 'attorney':
        input.attorneyApproverID = personID;
        input.attorneyApprovalDate = now;
        break;

      case 'national':
        input.nationalApproverID = personID;
        input.nationalApprovalDate = now;
        break;

      case 'final':
        input.finalCaseManagerApproverID = personID;
        input.finalCaseManagerApprovalDate = now;
        input.state = PreparedFormState.COMPLETED;
        break;

      default:
        throw new Error();
    }

    const queries: Array<Promise<any>> = [
      client.graphql({
        query: updatePreparedForm,
        variables: {
          input,
        },
      }) as Promise<any>,
    ];
    if (comment) {
      queries.push(createComment(personID, comment, formID));
    }

    queries.push(ActivityService.createActivity(
      {
        activityType: approverType === 'final'
          ? ActivityType.CASE_FORM_COMPLETED : ActivityType.CASE_FORM_APPROVED,
        actorID: personID,
        actorType: personType,
        directTargetID: formID,
        indirectTargetID: formID,
        targetString: formName,
        ownerID: thisCase.id!,
      },
      approverType === 'final'
        ? CaseService.getCaseTeamIDsExceptEM(thisCase)
        : [thisCase.caseManagerID],
    ));

    try {
      const [updatedForm] = (await Promise.all(queries)) as [
        GraphQLResult<CustomUpdatePreparedFormMutation>,
      ];

      if (updatedForm?.data?.updatePreparedForm) {
        return updatedForm.data.updatePreparedForm;
      }
      throw new Error('Form update failed.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
      throw err;
    }
  };

  changeAssignment = async (
    form: PreparedForm,
    assignedToID: string,
    newState: PreparedFormState,
  ) => {
    const response = (await client.graphql({
      query: updatePreparedForm,
      variables: {
        input: {
          id: form.id,
          assignedToID,
          assignedToEmployerID: assignedToID,
          assignedToNationalID: assignedToID,
          assignedToTeamMemberID: assignedToID,
          state: newState,
          updatedAt: new Date().toISOString(),
        },
      },
    })) as unknown as GraphQLResult<CustomUpdatePreparedFormMutation>;

    if (response?.data?.updatePreparedForm) {
      return response.data.updatePreparedForm;
    }

    throw new Error();
  };

  changeArchived = async (
    id: string,
    archived: boolean,
  ): Promise<Array<PreparedFormTemplate>> => {
    const currentTemplates = await this.getFormTemplates();

    const template = currentTemplates.find((t) => t.id === id);

    if (!template) {
      throw new Error();
    }

    template.archived = archived;

    const updatedTemplates = [
      ...currentTemplates.filter((t) => t.id !== id),
      template,
    ];

    await client.graphql({
      query: updateFirm,
      variables: {
        input: {
          id: FIRM_COMPANY.id,
          preparedFormTemplates: updatedTemplates,
        },
      },
    });

    return updatedTemplates;
  };

  createTemplate = async (newTemplate: PreparedFormTemplateInput) => {
    const currentTemplates = await this.getFormTemplates();

    const updatedTemplates = [...currentTemplates, newTemplate];

    await client.graphql({
      query: updateFirm,
      variables: {
        input: {
          id: FIRM_COMPANY.id,
          preparedFormTemplates: updatedTemplates,
        },
      },
    });

    return updatedTemplates;
  };

  getFormTemplates = async (): Promise<Array<PreparedFormTemplate>> => {
    const response = await getCacheWithExpiration(
      'prepared-form-templates',
      () => client.graphql({
        query: listPreparedFormTemplates,
        variables: { id: FIRM_COMPANY.id },
      }) as unknown as GraphQLResult<GetFirmQuery>,
      10 * 1000, // Use a short cache, 10s
    );

    return response.data?.getFirm?.preparedFormTemplates || [];
  };

  getStateText = (
    preparedForm: PreparedForm,
    thisCase: Case,
  ) => {
    const { state } = preparedForm;
    const personID = PersonService.getPersonID();
    if (!state) {
      return null;
    }

    if (state === PreparedFormState.REJECTED) {
      let rejecterName = '';

      if (preparedForm.rejectedByID === personID) {
        rejecterName = 'you';
      } else {
        rejecterName = PersonService.fullname(
          preparedForm.rejectedByNational || preparedForm.rejectedByTeamMember,
        );
      }
      const date = preparedForm.rejectedDate;

      return `Rejected by ${rejecterName} on
          ${shortOrLongDate(date!, 'MMM d', 'MMM d, yyyy')}`;
    }

    if (preparedForm.assignedToID === personID) {
      return <strong>Awaiting your approval</strong>;
    }

    if (state === PreparedFormState.AWAITING_ATTORNEY_APPROVAL) {
      return `Out to ${fullname(preparedForm.assignedToTeamMember)} for attorney review`;
    }

    if (state === PreparedFormState.AWAITING_NATIONAL_APPROVAL) {
      return 'Awaiting national’s review';
    }

    if (state === PreparedFormState.AWAITING_SPONSOR_APPROVAL) {
      return `Out to ${fullname(preparedForm.assignedToEmployer || preparedForm.assignedToEmployer)} for sponsor review`;
    }

    if (state === PreparedFormState.PREPARED) {
      if (this.missingApprovals(preparedForm)) {
        return 'Prepared and ready to be sent for review';
      }

      return thisCase.caseManagerID === personID
        ? <strong>Prepared and ready for your final approval</strong>
        : 'Prepared and ready for final case manager approval';
    }

    return stateResolver(state);
  };

  listComments = async (formID: string) => client.graphql({
    query: listFormComments,
    variables: { id: formID },
  });

  missingApprovals = (form: PreparedForm) => (
    (form.requiresNationalApproval && !form.nationalApproverID)
    || (form.requiresSponsorApproval && !form.sponsorApproverID)
    || (form.requiresAttorneyApproval && !form.attorneyApproverID)
  );

  reject = async (
    formID: string,
    comment: string,
    thisCase: Case,
    formName: string,
  ) => {
    const personID = PersonService.getPersonID();
    const personType = PersonService.getPersonType();
    const now = new Date().toISOString();

    const [updatedForm] = (await Promise.all([
      client.graphql({
        query: updatePreparedForm,
        variables: {
          input: {
            id: formID,
            assignedToID: null,
            attorneyApproverID: null,
            attorneyApprovalDate: null,
            sponsorApproverID: null,
            sponsorApproverEmployerID: null,
            sponsorApproverNationalID: null,
            sponsorApprovalDate: null,
            nationalApproverID: null,
            nationalApprovalDate: null,
            finalCaseManagerApproverID: null,
            finalCaseManagerApprovalDate: null,
            rejectedByID: personID,
            rejectedByEmployerID: personID,
            rejectedByNationalID: personID,
            rejectedByTeamMemberID: personID,
            rejectedDate: now,
            state: PreparedFormState.REJECTED,
            updatedAt: now,
          },
        },
      }),
      createComment(personID, comment, formID),
    ])) as unknown as [GraphQLResult<CustomUpdatePreparedFormMutation>];

    if (updatedForm.data?.updatePreparedForm) {
      await ActivityService.createActivity({
        activityType: ActivityType.CASE_FORM_REJECTED,
        actorID: personID,
        actorType: personType,
        targetString: formName || '',
        indirectTargetID: formID,
        ownerID: thisCase.id!,
      }, [thisCase.caseManagerID]);

      return updatedForm.data.updatePreparedForm;
    }

    throw new Error();
  };

  removeFromCase = (preparedForm: PreparedForm) => client.graphql({
    query: updatePreparedForm,
    variables: {
      input: {
        id: preparedForm.id,
        state: preparedForm.state,
        caseID: null,
      },
    },
  });

  reset = async (formID: string, thisCase: Case, formName?: string): Promise<PreparedForm> => {
    const personID = PersonService.getPersonID();
    const personType = PersonService.getPersonType();

    const response = (await client.graphql({
      query: updatePreparedForm,
      variables: {
        input: {
          id: formID,
          assignedToID: null,
          assignedToEmployerID: null,
          assignedToNationalID: null,
          assignedToTeamMemberID: null,
          preparerID: null,
          completedFormID: null,
          attorneyApprovalDate: null,
          attorneyApproverID: null,
          nationalApprovalDate: null,
          nationalApproverID: null,
          sponsorApprovalDate: null,
          sponsorApproverID: null,
          sponsorApproverEmployerID: null,
          sponsorApproverNationalID: null,
          finalCaseManagerApproverID: null,
          finalCaseManagerApprovalDate: null,
          rejectedDate: null,
          rejectedByID: null,
          rejectedByEmployerID: null,
          rejectedByNationalID: null,
          rejectedByTeamMemberID: null,
          state: PreparedFormState.TODO,
          updatedAt: new Date().toISOString(),
        },
      },
    })) as unknown as GraphQLResult<CustomUpdatePreparedFormMutation>;

    if (response.data?.updatePreparedForm) {
      await ActivityService.createActivity({
        activityType: ActivityType.CASE_FORM_RESET,
        actorID: personID,
        actorType: personType,
        targetString: formName || '',
        indirectTargetID: formID,
        ownerID: thisCase.id!,
      }, [thisCase.caseManagerID]);

      return response.data.updatePreparedForm as PreparedForm;
    }

    throw new Error();
  };

  unassign = async (id: string) => client.graphql({
    query: updatePreparedForm,
    variables: {
      input: {
        id,
        assignedToID: null,
        assignedToEmployerID: null,
        assignedToNationalID: null,
        assignedToTeamMemberID: null,
        state: PreparedFormState.PREPARED,
        updatedAt: new Date().toISOString(),
      },
    },
  });

  updateTemplate = async (updatedTemplate: PreparedFormTemplateInput) => {
    const currentTemplates = await this.getFormTemplates();

    const updatedTemplates = [
      ...currentTemplates.filter((t) => t.id !== updatedTemplate.id),
      updatedTemplate,
    ];

    await client.graphql({
      query: updateFirm,
      variables: {
        input: {
          id: FIRM_COMPANY.id,
          preparedFormTemplates: updatedTemplates,
        },
      },
    });

    return updatedTemplates;
  };
}

export default new PreparedFormService();
